import * as THREE from "three";

/**
 * GPU-based Dust Effect
 * All animation is done in the vertex shader - no CPU updates per frame
 */
export default class DustEffect {
  BASE_PARTICLE_COUNT = 5000;
  PARTICLE_SIZE = 0.04;

  particleSystem = undefined;
  startingLowerLimit = 0;  // Keep particles above floor level
  upperLimit = 2;

  group = new THREE.Group();

  constructor(intensity) {
    intensity = Math.min(Math.max(intensity, 0), 100);

    window.floorSize = new THREE.Vector3();
    window.floor.geometry.computeBoundingBox();
    window.floor.geometry.boundingBox.getSize(floorSize);

    this.setup(intensity);
  }

  setup(intensity) {
    if (intensity == this.currentIntensity) return;

    // Since the intensity has changed, we have to clean up
    let parent = this.group.parent;
    if (parent) {
      window.gu.time.value = 0;
      this.group.parent.remove(this.group);
      this.group = new THREE.Group();
      parent.add(this.group);
    }

    // Apply quality-based particle multiplier
    const particleMultiplier = window.qualitySettings?.get().particleMultiplier ?? 1;
    let count = Math.floor((this.BASE_PARTICLE_COUNT / 100) * intensity * particleMultiplier);
    
    if (count === 0) return;
    
    this.currentIntensity = intensity;

    const positions = new Float32Array(count * 3);
    const particleSizes = new Float32Array(count);
    const seeds = new Float32Array(count * 4); // x, y, z seed + speed multiplier

    for (let i = 0; i < count; i++) {
      // Initial position
      positions[i * 3] = (Math.random() - 0.5) * (floorSize.x - 0.2) + 0.3;
      positions[i * 3 + 1] = this.startingLowerLimit + Math.random() * (this.upperLimit - this.startingLowerLimit);
      positions[i * 3 + 2] = (Math.random() - 0.5) * (floorSize.z - 0.2) - 0.3;
      
      // Particle size
      particleSizes[i] = Math.random() * this.PARTICLE_SIZE + 0.02;
      
      // Random seeds for GPU animation (different phases and speeds per particle)
      seeds[i * 4] = Math.random() * 6.28318; // phase X
      seeds[i * 4 + 1] = Math.random() * 6.28318; // phase Y
      seeds[i * 4 + 2] = Math.random() * 6.28318; // phase Z
      seeds[i * 4 + 3] = 0.3 + Math.random() * 0.7; // speed multiplier
    }

    const particlesGeometry = new THREE.BufferGeometry();
    particlesGeometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    particlesGeometry.setAttribute("sizes", new THREE.BufferAttribute(particleSizes, 1));
    particlesGeometry.setAttribute("seed", new THREE.BufferAttribute(seeds, 4));

    // Store bounds for shader
    const bounds = {
      minY: this.startingLowerLimit,
      maxY: this.upperLimit,
      rangeY: this.upperLimit - this.startingLowerLimit,
    };

    let particlesMaterial = new THREE.PointsMaterial({
      transparent: true,
      depthTest: false,
      blending: THREE.AdditiveBlending,
      onBeforeCompile: (shader) => {
        shader.uniforms.time = window.gu.time;
        shader.uniforms.boundsMinY = { value: bounds.minY };
        shader.uniforms.boundsRangeY = { value: bounds.rangeY };
        
        shader.vertexShader = `
          uniform float time;
          uniform float boundsMinY;
          uniform float boundsRangeY;
          
          attribute float sizes;
          attribute vec4 seed;
          
          varying vec3 vColor;

          uniform float size;
          uniform float scale;
          
          #include <common>
          #include <color_pars_vertex>
          #include <fog_pars_vertex>
          #include <morphtarget_pars_vertex>
          #include <logdepthbuf_pars_vertex>
          #include <clipping_planes_pars_vertex>
          
          #ifdef USE_POINTS_UV
            varying vec2 vUv;
            uniform mat3 uvTransform;
          #endif
          
          // Simple noise function for organic movement
          float noise(float x) {
            return fract(sin(x * 12.9898) * 43758.5453);
          }
          
          void main() {
            #ifdef USE_POINTS_UV
              vUv = (uvTransform * vec3(uv, 1)).xy;
            #endif
            
            #include <color_vertex>
            vColor = vec3(30., 30., 30.) / 255.;
            
            #include <morphinstance_vertex>
            #include <morphcolor_vertex>
            #include <begin_vertex>
            
            // GPU-based animation
            float t = time * 0.15 * seed.w; // Time scaled by particle's speed
            
            // Organic floating motion using sine waves with different frequencies
            float dx = sin(t * 0.7 + seed.x) * 0.05 + sin(t * 1.3 + seed.x * 2.0) * 0.02;
            float dy = sin(t * 0.5 + seed.y) * 0.03 + t * 0.02; // Slow upward drift
            float dz = sin(t * 0.9 + seed.z) * 0.03 + sin(t * 1.7 + seed.z * 2.0) * 0.015;
            
            transformed.x += dx;
            transformed.z += dz;
            
            // Wrap Y position for continuous floating (mod-based wrapping)
            float newY = transformed.y + dy;
            transformed.y = boundsMinY + mod(newY - boundsMinY, boundsRangeY);
            
            #include <morphtarget_vertex>
            #include <project_vertex>
            
            gl_PointSize = size * sizes;
            
            #ifdef USE_SIZEATTENUATION
              bool isPerspective = isPerspectiveMatrix(projectionMatrix);
              if (isPerspective) gl_PointSize *= (scale / -mvPosition.z);
            #endif
            
            #include <logdepthbuf_vertex>
            #include <clipping_planes_vertex>
            #include <worldpos_vertex>
            #include <fog_vertex>
          }
        `;
        
        shader.fragmentShader = `
          varying vec3 vColor;
          uniform vec3 diffuse;
          uniform float opacity;
          
          #include <common>
          #include <color_pars_fragment>
          #include <map_particle_pars_fragment>
          #include <alphatest_pars_fragment>
          #include <alphahash_pars_fragment>
          #include <fog_pars_fragment>
          #include <logdepthbuf_pars_fragment>
          #include <clipping_planes_pars_fragment>
          
          void main() {
            #include <clipping_planes_fragment>
            
            float d = length(gl_PointCoord.xy - 0.5);
            vec4 diffuseColor = vec4(vColor, smoothstep(0.5, 0.1, d) * 0.5);
            vec3 outgoingLight = vec3(0.0);
            
            #include <logdepthbuf_fragment>
            #include <map_particle_fragment>
            #include <color_fragment>
            #include <alphatest_fragment>
            #include <alphahash_fragment>
            
            outgoingLight = diffuseColor.rgb;
            
            #include <opaque_fragment>
            #include <tonemapping_fragment>
            #include <colorspace_fragment>
            #include <fog_fragment>
            #include <premultiplied_alpha_fragment>
          }
        `;
      },
    });

    this.particleSystem = new THREE.Points(particlesGeometry, particlesMaterial);
    this.group.add(this.particleSystem);
    
    // No more CPU animation loop needed - GPU handles everything!
  }
}
